import { prefersReducedMotion } from '@theme/utilities';

/**
 * Reveal-on-scroll: fades + translates elements marked with [data-reveal] into
 * view as they enter the viewport. Respects prefers-reduced-motion (elements are
 * shown immediately, no motion). Plays once per element by default.
 *
 * Opt out globally by removing the `reveal-on-scroll-enabled` class from <body>
 * (driven by the theme setting). Use [data-reveal-delay="120"] on children for
 * staggered entrances.
 */

const VISIBLE_CLASS = 'reveal-visible';
const SELECTOR = '[data-reveal]';

function revealAll(root = document) {
  root.querySelectorAll(SELECTOR).forEach((el) => el.classList.add(VISIBLE_CLASS));
}

function init() {
  // Feature disabled via theme setting, or motion reduced: show everything, no observer.
  if (!document.body.classList.contains('reveal-on-scroll-enabled') || prefersReducedMotion()) {
    revealAll();
    return;
  }

  if (!('IntersectionObserver' in window)) {
    revealAll();
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.intersectionRatio >= 0.15) {
          entry.target.classList.add(VISIBLE_CLASS);
          observer.unobserve(entry.target);
        }
      }
    },
    { threshold: [0, 0.15], rootMargin: '0px 0px -8% 0px' }
  );

  const observe = (root = document) => root.querySelectorAll(SELECTOR).forEach((el) => observer.observe(el));
  observe();

  // Re-scan when sections are re-rendered by the theme editor / section hydration.
  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (target instanceof Element) observe(target);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
